import { computed, ref } from 'vue';
import { createEventHook } from '@vueuse/core';
import { useRouter } from 'vue-router/composables';

import { devLog } from '@/common/debug';
import { isRequestLimitError, parseErrorMessage, ErrorCode, isThresholdLimitError } from '@/common/error';
import { useBotUser } from '@/stores/useBotUser';
import { switcher } from '@/common/switcher';
import { AI_EVENTS, useEventLogger } from '@/composables/useEventLogger';

import type {
  ConnectPages,
  BaseConnectWizardEvent,
  TransactionInputEvent,
  DescriptionSubmittedEvent,
  VisitorInfoSubmittedEvent,
  ConnectToSupportEvent,
} from '../support-wizard';
import {
  ConnectWizardEvent,
  isConnectToSupportEvent,
  isTransactionInputEvent,
  isDescriptionSubmittedEvent,
  isVisitorInfoSubmittedEvent,
  ConnectRequestType,
  isConfirmConnectSupportRequestEvent,
} from '../support-wizard';
import type { ConnectToAgent, ResumeLiveChatExternEvent, UserSelectedConnectOptionEvent } from '../message/types';
import {
  createConnectOptionSelectMessage,
  createSystemMessage,
  createTransactionSelectMessage,
  createUserSystemMessage,
  getUserSelections,
  getVoiceChatExtraFields,
} from '../message/lib';
import { MessageType } from '../message/enum';
import { getConnectOptionChannelLabel } from '@/common/connectOption.utils';
import {
  getSingleSkipConnectOption,
  isSkipToLiveAgentFlow,
  resolveSingleSkipConnectOptionWithAgents,
} from '@/common/skipToLiveAgent.utils';
import * as ItemType from '@/common/bot/lib/ItemType';

import { usePleasedSupport } from './usePleasedSupport';
import { createSupportRequest, SupportType, type PleasedSupportKind } from './usePleasedSupport.lib';
import type { PlsDialogState, WidgetThresholdLimitModalState } from './usePleasedSupport.types';
import * as PleasedApi from '@/common/api/api.pleased';
import { BetHistoryEntity, TransStatement } from '@/common/api/model';
import { InteractionView, useChatWidget } from '@/stores/useChatWidget';
import { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { ConnectOptionKind, ConnectOptionType } from '@/common/bot/enum/ConnectOptionType';
import { MessageUtilities } from '../message/useMessageUtilities';
import { notEmpty } from '@/common/utils';
import { generateSelectedTransactionsTags, hasSelectedTransactions } from '@/common/selectedTransactions.utils';
import { useI18n, i18n } from '@/plugins/i18n';
import { store } from '@/store';
import { logger as globalLogger } from '@/common/api/logger';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { useVoiceAiHandover } from '@/stores/useVoiceAiHandover';
import { storeToRefs } from 'pinia';
import { addBreadcrumb, captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

enum WizardStatus {
  NotStarted,
  Ongoing,
  Completed,
}

const mapConnectOptionToType = (connectOption: ConnectOptionKind) => {
  const map: Record<ConnectOptionKind, ConnectRequestType> = {
    [ConnectOptionType.LIVE_CHAT]: ConnectRequestType.CHAT,
    [ConnectOptionType.VOICE_CALL]: ConnectRequestType.VOICE,
    [ConnectOptionType.VOICE_AI]: ConnectRequestType.VOICE_AI,
    [ConnectOptionType.OPEN_REQUEST]: ConnectRequestType.OPEN_REQUEST,
    [ConnectOptionType.FOLLOW_UP]: ConnectRequestType.FOLLOW_UP,
    [ConnectOptionType.CANCEL]: ConnectRequestType.OPEN_REQUEST, // Unused: CANCEL is handled before this is called
  };

  return map[connectOption] || ConnectRequestType.OPEN_REQUEST;
};

const mapConnectRequestTypeToSupportType = (type: ConnectRequestType): PleasedSupportKind | null => {
  switch (type) {
    case ConnectRequestType.CHAT:
      return SupportType.chat;
    case ConnectRequestType.VOICE:
      return SupportType.voice;
    case ConnectRequestType.OPEN_REQUEST:
      return SupportType.openRequest;
    default:
      return null;
  }
};

const resolveAiErrorTag = (isFirstRequest: boolean | undefined): string | null => {
  if (isFirstRequest === true) return 'ai::first_api_error';
  if (isFirstRequest === false) return 'ai::api_error';
  return null;
};

const buildSupportSubject = (category: string, section: string): string => {
  if (category.length === 0) return 'Support Request:';
  if (section.length === 0) return category;
  return `${category}+ ${section}`;
};

const buildAutoAddTags = (config: ConnectToAgent | null | undefined): string[] => {
  const { isFirstRequest, overlongConversation, selectedTransactionsData, appliedMetadataAutomation } = config || {};
  const selectedTransactionTags =
    selectedTransactionsData && hasSelectedTransactions(selectedTransactionsData)
      ? generateSelectedTransactionsTags(selectedTransactionsData)
      : [];

  return [
    appliedMetadataAutomation?.tag,
    resolveAiErrorTag(isFirstRequest),
    overlongConversation ? 'ai::exceed_msg_limit' : null,
    ...selectedTransactionTags,
  ].filter(notEmpty);
};

const buildChatTags = (metaTags: Record<string, string | number | boolean>, locale: string): string[] =>
  Object.entries({
    ...metaTags,
    language: locale,
  })
    .filter(([, v]) => String(v ?? '').trim().length > 0)
    .map(([k, v]) => `${k}=${String(v).trim()}`);

const resolveAiSessionId = (
  config: ConnectToAgent | null | undefined,
  fallbackSessionId: string | undefined
): string | undefined => {
  if (!config?.aiConversation) return undefined;
  return config.resumeAiSessionId || fallbackSessionId;
};

const mapConnectRequestTypeToThresholdChannel = (
  type: ConnectRequestType
): WidgetThresholdLimitModalState['requestedChannel'] => (type === ConnectRequestType.VOICE ? 'call' : 'liveChat');

const isThresholdConnectError = (err: unknown): boolean => {
  const errCode = (err as { code?: number })?.code;
  return isThresholdLimitError(err as Error) || (typeof errCode === 'number' && errCode === ErrorCode.THRESHOLD_LIMIT);
};

export enum RequestLimitModalChoice {
  MY_REQUESTS = 'my_requests',
  CANCEL = 'cancel',
}

// TODO: refactor this composable and interaction between event handlers & UI in general
// Maybe rename to useConnectToAgentFlow and just save intermediate state + create support request
export function useConnectToAgentWizard(conversation: MessageUtilities) {
  const { t } = useI18n();
  const requestLimitModalResolvedHook = createEventHook<RequestLimitModalChoice>();

  const createDefaultErrorActions = (): PlsDialogState['actions'] => [
    {
      id: 'retry',
      text: 'OK',
      color: 'primary',
      action: () => {
        errorDialog.value.open = false;
      },
    },
  ];

  // const connectOptionsConfig = ref<null | ChatBotConnectConfig>(null);
  // TODO: use from store
  const connectToAgentOptionsConfig = ref<null | ConnectToAgent>(null);

  const lastWizardEvent = ref<BaseConnectWizardEvent | null>(null);
  const currentStatus = ref(WizardStatus.NotStarted);
  const selectedConnectOption = ref<ConnectOptionKind | null>(null);

  const consumeLandingOpenRequestEntry = (): boolean => {
    const config = connectToAgentOptionsConfig.value;
    if (!config?.landingOpenRequest) {
      return false;
    }

    connectToAgentOptionsConfig.value = { ...config, landingOpenRequest: undefined };
    return true;
  };

  const clearLandingOpenRequestEntry = () => {
    const config = connectToAgentOptionsConfig.value;
    if (!config?.landingOpenRequest) {
      return;
    }

    connectToAgentOptionsConfig.value = { ...config, landingOpenRequest: undefined };
  };

  const errorDialog = ref<PlsDialogState>({
    open: false,
    content: 'Error',
    title: '',
    actions: createDefaultErrorActions(),
  });

  const createInitialThresholdLimitModalState = (): WidgetThresholdLimitModalState => ({
    open: false,
    requestedChannel: null,
    hasLiveChatOnlineAgents: true,
    hasCallOnlineAgents: true,
    liveChatWaitTimeDescription: '',
    callWaitTimeDescription: '',
  });

  const thresholdLimitModal = ref<WidgetThresholdLimitModalState>(createInitialThresholdLimitModalState());

  const closeThresholdLimitModal = () => {
    thresholdLimitModal.value = createInitialThresholdLimitModalState();
  };

  const state = ref({
    userDescription: '',
    userTransactions: [] as (TransStatement | BetHistoryEntity)[],
  });

  const support = usePleasedSupport();
  const voiceAiHandover = useVoiceAiHandover();

  const router = useRouter();
  const widgetStore = useChatWidget();

  const openSimpleErrorDialog = (content: string) => {
    errorDialog.value.open = true;
    errorDialog.value.title = '';
    errorDialog.value.content = content;
    errorDialog.value.actions = createDefaultErrorActions();
  };

  const createVoiceAiDialogOkAction = (onDismiss?: () => void): PlsDialogState['actions'][number] => ({
    id: 'ok',
    text: t('page.connect.overloaded.voice_ai.dialog.ok'),
    color: 'primary',
    action: () => {
      errorDialog.value.open = false;
      onDismiss?.();
    },
  });

  const openVoiceAiUnavailableDialog = (onDismiss?: () => void) => {
    errorDialog.value.open = true;
    errorDialog.value.title = t('page.connect.overloaded.voice_ai_unavailable_modal.title');
    errorDialog.value.content = t('page.connect.overloaded.voice_ai_unavailable_modal.message');
    errorDialog.value.actions = [createVoiceAiDialogOkAction(onDismiss)];
  };

  const openVoiceAiNewChatErrorDialog = ({ message, onDismiss }: { message: string; onDismiss?: () => void }) => {
    errorDialog.value.open = true;
    errorDialog.value.title = t('page.connect.overloaded.voice_ai.dialog.title');
    errorDialog.value.content = message;
    errorDialog.value.actions = [createVoiceAiDialogOkAction(onDismiss)];
  };

  const openRequestLimitErrorDialog = () => {
    widgetStore.setIsChatOverlayVisible(false);
    widgetStore.setInteractionView(InteractionView.NONE);
    errorDialog.value.title = '';
    errorDialog.value.content = t('page.active_conversation.over_limit_active_error');
    errorDialog.value.actions = [
      {
        id: RequestLimitModalChoice.MY_REQUESTS,
        text: t('global.my_requests'),
        color: 'primary',
        action: async () => {
          errorDialog.value.open = false;
          await requestLimitModalResolvedHook.trigger(RequestLimitModalChoice.MY_REQUESTS);
          router.push('/conversations').catch(() => undefined);
        },
      },
      {
        id: RequestLimitModalChoice.CANCEL,
        text: t('global.cancel'),
        color: 'secondary',
        action: async () => {
          errorDialog.value.open = false;
          support.reset();
          await requestLimitModalResolvedHook.trigger(RequestLimitModalChoice.CANCEL);
        },
      },
    ];
    errorDialog.value.open = true;
  };

  const logger = useEventLogger();
  const connectConfigStore = useConnectConfig();
  const { connectConfig } = storeToRefs(connectConfigStore);

  const startHook = createEventHook();
  const eventHook = createEventHook<BaseConnectWizardEvent>();

  const userStore = useBotUser();
  const { onAuth } = userStore;
  const { isAuthenticated, guestFilledDetails } = storeToRefs(userStore);

  onAuth(() => {
    // If authentication changes during on the flow page, we
    // need to take users to the correct page.
    if (currentStatus.value !== WizardStatus.Ongoing) return;

    const isLoggedIn = isAuthenticated.value;
    if (isLoggedIn) {
      return submitWizardAction({
        type: ConnectWizardEvent.loginSuccessful,
      });
    }
  });

  function editSelectedTransaction() {
    if (selectedConnectOption.value) return;
    widgetStore.setInteractionView(InteractionView.NONE);
    renderTransactionSelectorForUser();
  }

  // Submit events for Support Wizard
  function submitTransactionSelected(payload: TransactionInputEvent['payload']) {
    const { transactionType, transactions } = payload;

    submitWizardAction({
      type: ConnectWizardEvent.transactionSelected,
      payload: {
        transactionType,
        transactions,
      },
    });
  }

  function submitDescriptionSubmitted(payload: DescriptionSubmittedEvent['payload']) {
    submitWizardAction({
      type: ConnectWizardEvent.descriptionSubmitted,
      payload,
    });
  }

  // TODO: this can be separated on Event level passing here
  // confirm connect option + type or start connect request + type
  function submitSelectedConnectOption(payload: UserSelectedConnectOptionEvent['payload']) {
    // We mark selected option to record that user achieved this step
    selectedConnectOption.value = payload.connectOption;

    if (payload.connectOption === ConnectOptionType.CANCEL) {
      widgetStore.setInteractionView(InteractionView.NONE);
      return;
    }

    const type = mapConnectOptionToType(payload.connectOption);

    if (payload.confirmation) {
      submitWizardAction({
        type: ConnectWizardEvent.confirmConnectSupportRequest,
        payload: {
          requestType: type,
        },
      });

      return;
    }

    submitWizardAction({
      type: ConnectWizardEvent.connectRequest,
      payload: {
        ...payload,
        type,
      },
    });
  }

  function submitOpenRequestDescriptionSubmitted(payload: DescriptionSubmittedEvent['payload']) {
    const { description } = payload;
    state.value.userDescription = description;

    conversation.addItems(
      createUserSystemMessage({
        message: description,
      })
    );

    submitWizardAction({
      type: ConnectWizardEvent.connectRequest,
      payload: {
        type: ConnectRequestType.OPEN_REQUEST,
      },
    });
  }

  function renderDescriptionBoxForUser() {
    widgetStore.setInteractionView(InteractionView.SUPPORT_REQUEST_DESCRIPTION);
  }

  function renderOpenRequestDescriptionForUser() {
    widgetStore.setInteractionView(InteractionView.OPEN_REQUEST_DESCRIPTION);
  }

  function renderTransactionSelectorForUser() {
    conversation.addItems(
      createTransactionSelectMessage({
        config: { connect: connectToAgentOptionsConfig.value },
        txSubtypes:
          connectToAgentOptionsConfig.value?.transactionTypes?.map((type) => type as ExternalTransactionKind) || [],
        singleSelectOnly:
          connectToAgentOptionsConfig.value?.metadataRules &&
          connectToAgentOptionsConfig.value.metadataRules.length > 0,
      })
    );
  }

  async function tryAutoSelectSingleSkipChannel(): Promise<boolean> {
    const config = connectToAgentOptionsConfig.value;
    const singleBoOption = getSingleSkipConnectOption(config);

    if (!singleBoOption) {
      return false;
    }

    let optionToSelect = singleBoOption;

    if (singleBoOption === ConnectOptionType.LIVE_CHAT || singleBoOption === ConnectOptionType.VOICE_CALL) {
      const agents = await PleasedApi.getOnlineAgentsCheck({
        chat: singleBoOption === ConnectOptionType.LIVE_CHAT,
        voice: singleBoOption === ConnectOptionType.VOICE_CALL,
        ticketId: config?.ticketId,
      }).catch(() => ({
        onlineChatAgentsCheck: false,
        onlineCallAgentsCheck: false,
      }));

      optionToSelect = resolveSingleSkipConnectOptionWithAgents(config, agents) ?? singleBoOption;
    }

    selectedConnectOption.value = optionToSelect;

    conversation.addItems(
      createConnectOptionSelectMessage({
        ticketId: config?.ticketId,
        showLiveChat: config?.liveChat,
        showVoiceChat: config?.voiceChat,
        showOpenRequest: config?.openRequest,
        selectedOption: optionToSelect,
      })
    );

    conversation.addItems(
      createUserSystemMessage({
        type: MessageType.DEFAULT,
        message: getConnectOptionChannelLabel(optionToSelect),
      })
    );

    if (optionToSelect === ConnectOptionType.VOICE_CALL) {
      conversation.addItems(
        createSystemMessage({
          type: MessageType.VOICE_CONFIRMATION,
          message: t('page.voice.disclaimer') as string,
        })
      );
      submitSelectedConnectOption({ connectOption: optionToSelect, confirmation: true });
      return true;
    }

    if (optionToSelect === ConnectOptionType.OPEN_REQUEST) {
      conversation.addItems(
        createSystemMessage({
          type: MessageType.DEFAULT,
          message: t('page.connect.open_request.describe_problem_prompt') as string,
        })
      );
      submitSelectedConnectOption({ connectOption: optionToSelect, confirmation: true });
      return true;
    }

    submitSelectedConnectOption({ connectOption: optionToSelect });
    return true;
  }

  async function maybeAutoSelectSkipChannel(): Promise<boolean> {
    if (!isSkipToLiveAgentFlow(connectToAgentOptionsConfig.value)) {
      return false;
    }

    return tryAutoSelectSingleSkipChannel();
  }

  function removePendingConnectOptionMessages() {
    conversation.messages.value
      .filter((message) => ItemType.isConnectOptionSelectMessage(message) && !message.selectedOption)
      .forEach((message) => conversation.deleteItem(message));
  }

  async function renderConnectOptionsForUser() {
    widgetStore.setInteractionView(InteractionView.NONE);
    removePendingConnectOptionMessages();

    const didAutoSelectSkipChannel = await maybeAutoSelectSkipChannel();

    if (didAutoSelectSkipChannel) {
      return;
    }

    conversation.addItems(
      createConnectOptionSelectMessage({
        ticketId: connectToAgentOptionsConfig.value?.ticketId,
        showLiveChat: connectToAgentOptionsConfig.value?.liveChat,
        showVoiceChat: connectToAgentOptionsConfig.value?.voiceChat,
        showOpenRequest: connectToAgentOptionsConfig.value?.openRequest,
      })
    );
  }

  function advanceSkipToLiveAgentWizard() {
    if (!isAuthenticated.value && !guestFilledDetails.value) {
      renderGuestFormForUser();
      return;
    }

    renderConnectOptionsForUser();
  }

  function renderLoginForUser() {
    widgetStore.setInteractionView(InteractionView.LOGIN);
  }

  function renderGuestFormForUser() {
    widgetStore.setInteractionView(InteractionView.GUEST_FORM);
  }

  /** Next wizard UI after we know the user is authenticated (same branching as legacy `start` flow). */
  function advanceConnectWizardForAuthenticatedUser(connectToAgentConfig: ConnectToAgent | null) {
    if (isSkipToLiveAgentFlow(connectToAgentConfig) || connectToAgentConfig?.ticketId) {
      renderConnectOptionsForUser();
      return;
    }

    const showTxSelector =
      isAuthenticated.value &&
      !connectToAgentConfig?.appliedMetadataAutomation &&
      connectToAgentConfig?.transactionTypes?.length;

    if (showTxSelector) {
      renderTransactionSelectorForUser();
      return;
    }

    const showDescriptionBox = connectToAgentConfig?.showDescriptionBox ?? true;

    if (showDescriptionBox) {
      renderDescriptionBoxForUser();
      return;
    }

    renderConnectOptionsForUser();
  }

  function resolveInitialWizardPage(connectToAgentConfig: ConnectToAgent | null) {
    if (isSkipToLiveAgentFlow(connectToAgentConfig)) {
      advanceSkipToLiveAgentWizard();
      return;
    }

    const forceLoginForAiAccount =
      connectToAgentConfig?.aiMandatoryAccountLogin === true && connectToAgentConfig?.aiConversation === true;

    if (!isAuthenticated.value && (!guestFilledDetails.value || forceLoginForAiAccount)) {
      renderLoginForUser();
      return;
    }

    if (connectToAgentConfig?.landingOpenRequest) {
      renderOpenRequestDescriptionForUser();
      return;
    }

    advanceConnectWizardForAuthenticatedUser(connectToAgentConfig);
  }

  function handleLoginSuccessfulNextStep() {
    if (consumeLandingOpenRequestEntry()) {
      renderOpenRequestDescriptionForUser();
      return;
    }
    // For AI conversation, logic/next steps will be handled in useAiFlowEventHandlers.ts
    if (connectToAgentOptionsConfig.value?.aiConversation) {
      // Reset the wizard status, as it's start of created Guest user session
      currentStatus.value = WizardStatus.NotStarted;

      // We need to return input back for recently logged in user by AI request
      if (connectToAgentOptionsConfig.value?.aiMandatoryAccountLogin) {
        widgetStore.setInteractionView(InteractionView.INPUT);
      }

      return;
    }

    advanceConnectWizardForAuthenticatedUser(connectToAgentOptionsConfig.value);
  }

  function handleContinueAsGuestNextStep() {
    renderGuestFormForUser();
  }

  function handleGoBackToLoginFromGuestNextStep() {
    renderLoginForUser();
  }

  function handleGuestInfoFilledNextStep() {
    if (consumeLandingOpenRequestEntry()) {
      renderOpenRequestDescriptionForUser();
      return;
    }

    if (isSkipToLiveAgentFlow(connectToAgentOptionsConfig.value)) {
      renderConnectOptionsForUser();
      return;
    }

    // For AI conversation, logic/next steps will be handled in useAiFlowEventHandlers.ts
    if (connectToAgentOptionsConfig.value?.aiConversation) {
      // Reset the wizard status, as it's start of created Guest user session
      currentStatus.value = WizardStatus.NotStarted;
      return;
    }

    advanceConnectWizardForAuthenticatedUser(connectToAgentOptionsConfig.value);
  }

  // UI Change handlers
  function handleTransactionSelectedNextStep() {
    if (connectToAgentOptionsConfig.value?.showDescriptionBox) {
      renderDescriptionBoxForUser();
    } else {
      renderConnectOptionsForUser();
    }
  }

  function handleDescriptionSubmittedNextStep() {
    renderConnectOptionsForUser();
  }

  function handleOpenRequestConfirmationNextStep() {
    renderOpenRequestDescriptionForUser();
  }

  // No render needed for connect option selected / follow up, as it's final step

  // State handlers
  // TODO: remove it and just save in connect config
  function handleTransactionSelected(event: TransactionInputEvent) {
    const { transactions } = event.payload;
    state.value.userTransactions = transactions;
  }

  function handleDescriptionSubmitted(event: DescriptionSubmittedEvent) {
    const { description } = event.payload;
    state.value.userDescription = description;
    userStore.setGuestDetail({
      description: description || '',
    });
  }

  function handleVisitorInfoSubmitted(event: VisitorInfoSubmittedEvent) {
    const { description, visitorInfo } = event.payload;
    state.value.userDescription = description || '';

    if (!visitorInfo) {
      return;
    }

    const { displayName, email, name, phone } = visitorInfo;
    const resolvedName = name || displayName || '';

    userStore.setGuestDetail({
      email,
      name: resolvedName,
      defaultName: displayName || resolvedName,
      phone: phone || '',
      description: description || '',
    });
  }

  function handleAbortCancelRequest() {
    if (selectedConnectOption.value) {
      // For flow with select channel, we show channels again
      selectedConnectOption.value = null;
      renderConnectOptionsForUser();
      return;
    }

    // For restart before selecting channel, we start the flow again
    start({ connectToAgentConfig: connectConfig.value });
    return;
  }

  function start(data: { connectToAgentConfig: ConnectToAgent | null }) {
    const { connectToAgentConfig } = data;

    connectToAgentOptionsConfig.value = connectToAgentConfig;
    currentStatus.value = WizardStatus.Ongoing;

    resolveInitialWizardPage(connectToAgentConfig);

    if (!connectToAgentConfig?.landingOpenRequest) {
      startHook.trigger(true);
    }
  }

  function startLandingOpenRequest(connectToAgentConfig: ConnectToAgent) {
    start({
      connectToAgentConfig: {
        ...connectToAgentConfig,
        landingOpenRequest: true,
      },
    });
  }

  const nextPageSwitcher = switcher()
    .when(ConnectWizardEvent.loginSuccessful, handleLoginSuccessfulNextStep)
    .when(ConnectWizardEvent.continueAsGuest, handleContinueAsGuestNextStep)
    .when(ConnectWizardEvent.goBack, handleGoBackToLoginFromGuestNextStep)
    .when(ConnectWizardEvent.transactionSelected, handleTransactionSelectedNextStep)
    .when(ConnectWizardEvent.guestInfoFilled, handleGuestInfoFilledNextStep)
    .when(ConnectWizardEvent.descriptionSubmitted, handleDescriptionSubmittedNextStep)
    .none((evt) => {
      globalLogger.error('[SUBMIT_WIZARD_ACTION] Invalid event found');
    });

  function completeWizard(event?: BaseConnectWizardEvent) {
    currentStatus.value = WizardStatus.Completed;
    event && eventHook.trigger(event);
  }

  /**
   * Wizard steps end in a support request (chat / call / ticket), so a failure here is a visitor
   * who never reached an agent. Channel-specific captures (`OpenChat`) win — `captureEventException`
   * skips an error that was already reported.
   */
  async function submitWizardAction(event: BaseConnectWizardEvent) {
    addBreadcrumb({
      category: 'chatbot.connect',
      message: event.type,
      level: 'info',
      data: { type: event.type },
    });

    try {
      return await runWizardAction(event);
    } catch (error) {
      captureEventException(error, ErrorTrackingEvent.ConnectRequest);
      throw error;
    }
  }

  const populateThresholdLimitModalAgents = async () => {
    try {
      const cfg = connectConfig.value;
      const agents = await PleasedApi.getOnlineAgentsCheck({
        chat: cfg?.liveChat ?? true,
        voice: cfg?.voiceChat ?? true,
      });
      thresholdLimitModal.value.hasLiveChatOnlineAgents = Boolean(agents.onlineChatAgentsCheck);
      thresholdLimitModal.value.hasCallOnlineAgents = Boolean(agents.onlineCallAgentsCheck);
    } catch {
      thresholdLimitModal.value.hasLiveChatOnlineAgents = true;
      thresholdLimitModal.value.hasCallOnlineAgents = true;
    }
  };

  const openThresholdLimitModal = async (requestType: ConnectRequestType) => {
    await populateThresholdLimitModalAgents();
    thresholdLimitModal.value.requestedChannel = mapConnectRequestTypeToThresholdChannel(requestType);
    thresholdLimitModal.value.open = true;
  };

  const handleConnectSupportError = async (err: unknown, requestType: ConnectRequestType) => {
    if (isThresholdConnectError(err)) {
      await openThresholdLimitModal(requestType);
      throw err;
    }

    if (err instanceof Error && isRequestLimitError(err)) {
      openRequestLimitErrorDialog();
      return undefined;
    }

    openSimpleErrorDialog(`${err}. You can retry make the same request again.`);
    throw err;
  };

  const buildConnectSupportRequest = (supportType: PleasedSupportKind) => {
    const messageList = [...conversation.messages.value];
    const connectConfigOptions = connectToAgentOptionsConfig.value;
    const sessionId = logger.getSessionId();
    const aiSessionId = resolveAiSessionId(connectConfigOptions, sessionId);
    // Stamp pending Voice AI handoff on first create; skip when follow-up already has ticketId.
    const aiVoiceChatSessionId = connectConfigOptions?.ticketId
      ? undefined
      : (voiceAiHandover.pendingAiSessionId ?? undefined);
    const category = connectConfigOptions?.category?.trim() ?? '';
    const section = connectConfigOptions?.section?.trim() ?? '';

    return createSupportRequest({
      type: supportType,
      payload: {
        subject: state.value.userDescription || buildSupportSubject(category, section),
        description: state.value.userDescription,
        ticketId: connectConfigOptions?.ticketId,
        autoAddTags: buildAutoAddTags(connectConfigOptions),
        chatTags: buildChatTags(store.state.user.metaTags, i18n.locale),
        transactions: state.value.userTransactions,
        ...(aiSessionId ? { aiSessionId } : {}),
        ...(aiVoiceChatSessionId ? { aiVoiceChatSessionId } : {}),
        extraFields: getVoiceChatExtraFields(messageList),
        userSelections: getUserSelections(messageList),
        // Reflect the real visitor state: a guest (not authenticated) must be sent
        // as isGuest:true, otherwise the backend rejects it ("You're not allowed to
        // update this user") — this surfaced on the guest voice-call connect.
        isGuest: !isAuthenticated.value,
        chatbotSessionId: sessionId,
      },
    });
  };

  const handleConnectToSupport = async (event: ConnectToSupportEvent) => {
    const supportType = mapConnectRequestTypeToSupportType(event.payload.type);
    if (!supportType) {
      return completeWizard(event);
    }

    // TODO: pass wizard messages somehow here
    // When connect requested, then start connection flow.
    const request = buildConnectSupportRequest(supportType);
    const result = await support
      .connect(request)
      .catch((err: unknown) => handleConnectSupportError(err, event.payload.type));

    if (!result) return;
    // Connect was successful: consume the remembered Voice AI handoff session so it can't leak
    // into a later request, and close the action wizard.
    voiceAiHandover.clearHandoff();
    return completeWizard(event);
  };

  const shouldStopAfterConfirmConnect = (event: BaseConnectWizardEvent): boolean => {
    if (!isConfirmConnectSupportRequestEvent(event)) return false;
    if (event.payload.requestType === ConnectRequestType.OPEN_REQUEST) {
      handleOpenRequestConfirmationNextStep();
      return false;
    }
    return true;
  };

  const processWizardSubPageEvent = (event: BaseConnectWizardEvent) => {
    // Other "Sub-Pages" events are only for widget, so handled here
    if (isTransactionInputEvent(event)) {
      handleTransactionSelected(event);
    }

    if (isVisitorInfoSubmittedEvent(event)) {
      handleVisitorInfoSubmitted(event);
    }

    if (isDescriptionSubmittedEvent(event)) {
      handleDescriptionSubmitted(event);
    }

    if (shouldStopAfterConfirmConnect(event)) return;

    // Relay the events again
    eventHook.trigger(event);
    lastWizardEvent.value = event;

    // Note: We use the switcher() here to determine the next page
    // But the Developer can directly write if conditions here if required.
    // using switcher() is a personal preference of each developer
    // @see docs of switcher()
    nextPageSwitcher.match(event.type);
  };

  async function runWizardAction(event: BaseConnectWizardEvent) {
    // The Login event can be submitted multiple times. Current
    // implementation does not have any issues with that, pleased make sure that
    // add new functionality does not break that flow.

    // Connect to support events is shared between widget/conversation pages
    // So UI changes should be handled in the components itself
    if (isConnectToSupportEvent(event)) {
      return handleConnectToSupport(event);
    }

    processWizardSubPageEvent(event);
  }

  function reset() {
    state.value = {
      userDescription: '',
      userTransactions: [],
    };
    lastWizardEvent.value = null;
    currentStatus.value = WizardStatus.NotStarted;
    clearLandingOpenRequestEntry();
    thresholdLimitModal.value = createInitialThresholdLimitModalState();
    support.reset();
  }

  /**
   * Handles AI chat intervention - loads an existing chat when agent takes over from AI
   * @param chatId - The chat ID from the intervention event
   * @param firebaseToken - Firebase token for authentication
   */
  async function handleIntervention(params: { chatId: number; firebaseToken: string }) {
    const { chatId, firebaseToken } = params;

    globalLogger.debug('[CONNECT_REQUEST_EVENT] Handling AI intervention', { chatId });

    try {
      await support.chat.loadExistingChat({
        chatId,
        firebaseToken,
        fetchChatSnapshot: true,
      });

      // Complete the wizard
      currentStatus.value = WizardStatus.Completed;

      logger.track({
        name: AI_EVENTS.AI_INTERVENE_CHAT,
        metadata: { chatId },
      });
      devLog.debug('[CONNECT_REQUEST_EVENT] AI intervention chat loaded successfully', { chatId });
    } catch (error) {
      globalLogger.error('[CONNECT_REQUEST_EVENT] Error handling AI intervention', { error: parseErrorMessage(error) });
      openSimpleErrorDialog(`Agent failed to connect to your chat: ${parseErrorMessage(error)}`);
      throw error;
    }
  }

  /**
   * Handles resuming an existing live chat - loads a chat when user resumes from resumable chat modal
   * @param chatId - The chat ID from the resumable chat
   * @param firebaseToken - Firebase token for authentication
   */
  async function handleResumeChat(params: ResumeLiveChatExternEvent['payload']) {
    const { chatId, firebaseToken, timeSinceTriggers } = params;

    globalLogger.debug('[CONNECT_REQUEST_EVENT] Handling resume live chat', { chatId });

    try {
      // Load the resumed chat using the support system
      await support.chat.loadExistingChat({
        chatId,
        firebaseToken,
        timeSinceTriggers,
        generateSessionId: true,
        resumeMessage: t('resumable_chat.user_message') as string,
      });

      // Complete the wizard
      currentStatus.value = WizardStatus.Completed;

      logger.track({
        name: AI_EVENTS.RESUME_LIVE_CHAT,
        metadata: { chatId },
      });
      globalLogger.debug('[CONNECT_REQUEST_EVENT] Live chat resumed', { chatId });
    } catch (error) {
      globalLogger.error('Error resuming live chat', { error: error?.message });
      openSimpleErrorDialog(`Failed to resume your chat: ${error}`);
      throw error;
    }
  }

  return {
    connectToAgentOptionsConfig,
    support: { ...support },
    errorDialog,
    thresholdLimitModal,
    closeThresholdLimitModal,

    description: computed(() => state.value.userDescription),
    isConnectOngoing: computed(() => currentStatus.value === WizardStatus.Ongoing),

    submitWizardAction,
    editSelectedTransaction,
    submitTransactionSelected,
    submitDescriptionSubmitted,
    submitSelectedConnectOption,
    submitOpenRequestDescriptionSubmitted,

    handleAbortCancelRequest,
    handleIntervention,
    handleResumeChat,

    onStart: startHook.on,
    onRequestLimitModalResolved: requestLimitModalResolvedHook.on,
    onWizardEvent: eventHook.on,
    startWizard: start,
    startLandingOpenRequest,
    openVoiceAiUnavailableDialog,
    openVoiceAiNewChatErrorDialog,
    reset,
  };
}
